import { TranslateSDK } from './sdk';
import { CacheManager } from './service/cache-manager';
import { ConfigManager } from './core/config';
import { CACHE_TYPE } from './types';
import type {
  SDKConfig,
  PageTranslateConfig,
  ParagraphTranslateConfig,
  TranslatePlugin,
  CacheOptions,
  CacheConfig,
} from './types';
import { gateEntryConfig, isCacheEntryEnabled } from './entry-gate';

export class AliTranslateFacade {
  private sdk: TranslateSDK | null = null;
  private registeredPlugins = new Map<string, new () => TranslatePlugin>();
  private cacheManager: CacheManager | null = null;

  registerPlugin(name: string, PluginClass: new () => TranslatePlugin): void {
    this.registeredPlugins.set(name, PluginClass);
    if (this.sdk) {
      this.sdk.addPlugin(new PluginClass());
    }
  }

  async pageTranslate(config: SDKConfig & PageTranslateConfig): Promise<void> {
    this.rebuildIfNeeded(config);
    return this.sdk!.pageTranslate(config);
  }

  async paragraphTranslate(config: SDKConfig & ParagraphTranslateConfig): Promise<void> {
    this.rebuildIfNeeded(config);
    return this.sdk!.paragraphTranslate(config);
  }

  destroy(): void {
    if (this.sdk) {
      this.sdk.destroy();
      this.sdk = null;
    }
  }

  removeTranslations(): void {
    this.sdk?.removeTranslations();
  }

  get storage() {
    return {
      set: (
        source: string | Array<{ source: string; targets?: Record<string, string> }>,
        targets?: Record<string, string>,
      ) => {
        if (!isCacheEntryEnabled()) return;
        if (!this.cacheManager) {
          throw new Error('请先调用 pageTranslate 初始化，再使用 storage.set');
        }
        if (typeof source === 'string') {
          this.cacheManager.set(source, targets);
        } else {
          this.cacheManager.set(source);
        }
      },
      clear: (cacheOptions?: CacheOptions) => {
        if (!isCacheEntryEnabled()) return;
        if (cacheOptions) {
          const resolved: CacheConfig = {
            enabled: true,
            type: cacheOptions.type ?? CACHE_TYPE.LOCAL_STORAGE,
            expireTime: cacheOptions.expireTime ?? -1,
          };
          new CacheManager(resolved).clear();
        } else if (this.cacheManager) {
          this.cacheManager.clear();
        } else {
          throw new Error('请先调用 pageTranslate 初始化，或传入 cacheOptions 参数');
        }
      },
    };
  }

  private rebuildIfNeeded(config: SDKConfig): void {
    const gated = gateEntryConfig(config);

    if (this.sdk) {
      this.sdk.destroy();
      this.sdk = null;
    }

    const configManager = new ConfigManager(gated);
    this.cacheManager = isCacheEntryEnabled()
      ? new CacheManager(configManager.cache)
      : null;

    this.sdk = new TranslateSDK({
      ...gated,
      plugins: [...this.registeredPlugins.values()].map(P => new P()),
      ...(this.cacheManager ? { cacheManager: this.cacheManager } : {}),
    });
  }
}

const facade = new AliTranslateFacade();

if (typeof window !== 'undefined') {
  (window as any).AliTranslate = facade;
  (window as any).__AliTranslate = facade;
}
